import type { CachedSupplyPlanSnapshot, SupplyPlanSnapshotCacheMeta } from "./supply-plan-snapshot-cache";

/** One decoded body, shared by remounted blocks in this tab. The byte limit
 * measures its source JSON, not JS heap usage; the object is shared, not copied.
 * Server freshness/access verification still runs on every real opening. */
export const SUPPLY_PLAN_MEMORY_MAX_BYTES = 8 * 1024 * 1024;
export const SUPPLY_PLAN_MEMORY_TTL_MS = 5 * 60_000;

export function createSupplyPlanMemoryCache(persistedMaxAgeMs: number) {
  let retained: CachedSupplyPlanSnapshot | null = null;
  let expiresAt = 0;
  let version = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const clear = () => {
    version++;
    retained = null;
    expiresAt = 0;
    if (timer !== undefined) clearTimeout(timer);
    timer = undefined;
  };
  return {
    get version() { return version; },
    clear,
    read(key: string, identity: string): CachedSupplyPlanSnapshot | null {
      if (!retained) return null;
      const now = Date.now();
      if (retained.identity !== identity || now >= expiresAt || now < retained.savedAt
        || now - retained.savedAt >= persistedMaxAgeMs) {
        clear();
        return null;
      }
      return retained.key === key ? { ...retained, usedAt: now } : null;
    },
    /** An older async decode must not replace a newer save, mark or logout. */
    remember(entry: CachedSupplyPlanSnapshot, bytes: number, expectedVersion = version): boolean {
      if (expectedVersion !== version) return false;
      clear();
      const now = Date.now();
      if (!Number.isFinite(bytes) || bytes <= 0 || bytes > SUPPLY_PLAN_MEMORY_MAX_BYTES
        || !Number.isFinite(entry.calculatedAt) || entry.calculatedAt <= 0
        || entry.snapshot.calculatedAt !== entry.calculatedAt
        || !Number.isFinite(entry.savedAt) || entry.savedAt > now || now - entry.savedAt >= persistedMaxAgeMs) return false;
      retained = entry;
      expiresAt = Math.min(now + SUPPLY_PLAN_MEMORY_TTL_MS, entry.savedAt + persistedMaxAgeMs);
      timer = setTimeout(clear, expiresAt - now);
      return true;
    },
    mark(entry: SupplyPlanSnapshotCacheMeta, current: boolean): void {
      // A metadata write can overlap the first decode, while RAM is empty.
      // That pending read must not republish its older freshness flag either.
      version++;
      if (retained?.key !== entry.key || retained.identity !== entry.identity || retained.calculatedAt !== entry.calculatedAt) return;
      retained = { ...retained, current };
    },
  };
}
